/*
  Softness, Unravelled — scroll-driven wool-ball canvas, depth rail, chip/lightbox
  modals and typewriter interludes. Adapted from a standalone HTML export
  (Claude Projects/sub pages/final pages/how-we-build-better-fibres.html) for
  sections/how-we-build-better-fibres.liquid.

  Two changes from the original export:
  1. syncHeaderHeight() measures the theme's real (sticky) header and exposes it
     as --rd-fibres-header-h so the fixed HUD sits below it instead of under it.
  2. The custom cursor dot only shows while the pointer is over this page's own
     content — otherwise it would double up with the normal cursor over the
     site header/footer, which sit outside this section's wrapper.
*/
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wrapper = document.querySelector('.rd-fibres');

  /* ================================================================
     MODAL IMAGE SWAP (chipModal + lightbox both reuse one <img> across
     many different photos) -- setting .src alone doesn't clear what's
     currently painted, so the PREVIOUS photo stays visible until the new
     one finishes loading. hideModalImg() drops opacity to 0 with the
     transition disabled (an instant, invisible reset, not a fade-to-
     blank), then revealModalImg() re-enables the transition and fades
     the new photo in once it's actually ready -- so the old photo is
     never on screen while the new one loads.
  ================================================================ */
  function hideModalImg(imgEl) {
    imgEl.style.transitionDuration = '0s';
    imgEl.classList.remove('is-loaded');
  }
  function revealModalImg(imgEl) {
    imgEl.style.transitionDuration = '';
    imgEl.classList.add('is-loaded');
  }

  /* ================================================================
     HEADER HEIGHT SYNC (see assets/meet-our-sheep-stations.js for the
     same pattern) — keeps the fixed HUD clear of the sticky header.
  ================================================================ */
  function syncHeaderHeight(){
    const header = document.querySelector('.header-wrapper');
    if (!wrapper) return;
    const apply = () => {
      wrapper.style.setProperty('--rd-fibres-header-h', `${header ? header.getBoundingClientRect().height : 0}px`);
    };
    apply();
    if (header && 'ResizeObserver' in window) {
      new ResizeObserver(apply).observe(header);
    } else {
      window.addEventListener('resize', apply);
    }
  }
  syncHeaderHeight();

  // ─── Cursor ───
  const cursor = document.getElementById('cursor');
  let cx = 0, cy = 0, tx = 0, ty = 0;
  window.addEventListener('pointermove', e => { tx = e.clientX; ty = e.clientY; });
  (function cursorLoop() {
    cx += (tx - cx) * 0.20;
    cy += (ty - cy) * 0.20;
    cursor.style.transform = `translate(${cx}px, ${cy}px) translate(-50%,-50%)`;
    requestAnimationFrame(cursorLoop);
  })();
  if (wrapper) {
    wrapper.addEventListener('pointerenter', () => cursor.classList.add('show'));
    wrapper.addEventListener('pointerleave', () => cursor.classList.remove('show'));
  }

  // ─── Refs ───
  const chapters    = Array.from(document.querySelectorAll('.chapter'));
  const atmosphere  = document.getElementById('atmosphere');
  const heroBg      = document.getElementById('heroBg');
  const depthEl     = document.getElementById('depth');
  const tickerEl    = document.getElementById('ticker');
  const scrollBar   = document.querySelector('.scroll-bar');
  const outro       = document.getElementById('outro');
  const returnBtn   = document.getElementById('returnBtn');

  if (returnBtn) {
    returnBtn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  const materialByChapter = chapters.map(ch => ch.querySelector('.material'));
  const figsByChapter     = chapters.map(ch => ch.querySelector('.m-figs'));

  // ═══════════════════════════════════════════════════════
  //  THE BALL OF WOOL
  // ═══════════════════════════════════════════════════════
  const cvs = document.getElementById('wool');
  const ctx = cvs.getContext('2d');
  let W = 0, H = 0, DPR = 1;
  let BX = 0, BY = 0, R0 = 100;          // ball centre + base radius
  let time = 0;
  let unravel = 0;                        // 0 wound → 1 gone
  let spin = 0;
  let lastScroll = 0;
  let heroP = 0;                          // 0 in hero → 1 once journey begins

  const LOOPS = 120;
  let loops = [];

  function sizeCanvas() {
    DPR = Math.min(devicePixelRatio || 1, 2);
    W = innerWidth; H = innerHeight;
    cvs.width = W * DPR; cvs.height = H * DPR;
    cvs.style.width = W + 'px'; cvs.style.height = H + 'px';
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    const mobile = W < 720;
    R0 = Math.max(54, Math.min(W * 0.075, 118));
    if (mobile) R0 = Math.max(38, W * 0.105);
  }
  // The ball rests centred & small over the hero, then settles into its
  // journey position: on desktop it drifts to the RIGHT so the reading column
  // has the left half to itself; on mobile it settles into the TOP third so
  // the reading column sits cleanly below it.
  function ballPos() {
    const mobile = W < 720;
    const restY = mobile ? 0.28 : 0.34;
    const homeY = mobile ? 0.21 : 0.50;
    const restX = 0.5;
    const homeX = 0.5;   /* stay centred on all breakpoints */
    BY = H * (restY + (homeY - restY) * heroP);
    BX = W * (restX + (homeX - restX) * heroP);
    return R0 * (0.7 + 0.3 * heroP);
  }

  function buildBall() {
    loops = [];
    for (let i = 0; i < LOOPS; i++) {
      // radii span from small (interior) to full (surface) so the strands
      // build a dense, filled ball rather than a hollow ring
      const t = Math.random();
      loops.push({
        rot:   Math.random() * Math.PI,
        rr:    0.34 + t * 0.66,                      // 0.34 → 1.0 radius ratio
        sq:    0.44 + Math.random() * 0.48,          // squash (varied wrap angles)
        jx:    (Math.random() - 0.5) * 0.14,
        jy:    (Math.random() - 0.5) * 0.14,
        w:     0.9 + Math.random() * 1.3,            // strand thickness
        a:     0.9 + Math.random() * 0.1,            // near-opaque → a solid ball, no see-through
        dir:   Math.random() < 0.5 ? 1 : -1,
        ph:    Math.random() * Math.PI * 2,
        tone:  Math.random(),                        // cream ↔ tan variation
      });
    }
  }

  function loopColor(tone, alpha) {
    // soft tan → cream range: #d0c6aa → #f8f0e4 (gives wound-yarn depth)
    const r = 208 + tone * 40, g = 198 + tone * 42, b = 170 + tone * 58;
    return `rgba(${r|0},${g|0},${b|0},${alpha})`;
  }

  // Draw one winding as a wobbling ellipse; frac<1 draws a partial arc.
  // Returns the end point when partial (for the tail to pick up).
  function drawLoop(L, R, frac) {
    const steps = 44;
    const n = Math.max(2, Math.round(steps * frac));
    const rx = R * L.rr, ry = R * L.rr * L.sq;
    const rot = L.rot + spin * L.dir;
    const cosR = Math.cos(rot), sinR = Math.sin(rot);
    const cxL = BX + L.jx * R, cyL = BY + L.jy * R;
    ctx.strokeStyle = loopColor(L.tone, L.a);
    ctx.lineWidth = L.w;
    ctx.beginPath();
    let ex = 0, ey = 0;
    for (let j = 0; j <= n; j++) {
      const a = (j / steps) * Math.PI * 2 + L.ph;
      const wob = Math.sin(a * 3 + time * 0.8 + L.ph) * R * 0.015;
      const px0 = (rx + wob) * Math.cos(a);
      const py0 = (ry + wob) * Math.sin(a);
      const x = cxL + px0 * cosR - py0 * sinR;
      const y = cyL + px0 * sinR + py0 * cosR;
      j === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      ex = x; ey = y;
    }
    ctx.stroke();
    return [ex, ey];
  }

  // ONE continuous strand. It starts wound around the ball as a spiral (several
  // turns, spiralling out from the core to the surface), and its free end lifts
  // off the top and climbs the page. Because it is a single unbroken path, the
  // ball and the loose thread are unmistakably the same piece of yarn — as the
  // page scrolls the spiral loses turns and the free length grows: it unwinds.
  function drawTail(cx, cy, R, p) {
    if (p <= 0.005) return;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // wound turns unwind away as the page progresses; the free length climbs fast
    const turns = Math.max(0.5, 2.6 * (1 - p * 0.8));
    const climb = (cy + 260) * Math.min(1, p * 5);

    // the strand peels off near the top of the ball; the peel point (and the
    // whole wound spiral) rotates with the ball's spin so it reads as unwinding
    const peelAng = -Math.PI / 2 + spin;

    const pts = [];

    // ─ wound section: core → surface, a spiral that ends exactly at the peel point ─
    const woundSteps = Math.max(30, Math.round(turns * 26));
    let peelX = cx, peelY = cy - R;
    if (R > 3) {
      for (let j = 0; j <= woundSteps; j++) {
        const u = j / woundSteps;                       // 0 core … 1 peel point
        const ang = peelAng - (1 - u) * turns * Math.PI * 2;
        const rr  = R * (0.16 + 0.84 * u);              // spiral outward to the surface
        const wob = Math.sin(u * turns * 6.3 + time * 0.5) * R * 0.02;
        const x = cx + Math.cos(ang) * (rr + wob);
        const y = cy + Math.sin(ang) * (rr + wob) * 0.92;   // slight squash = 3D feel
        pts.push([x, y]);
        if (j === woundSteps) { peelX = x; peelY = y; }
      }
    }

    // ─ free section: from the peel point, easing onto the ball's centre-line
    //   and rising straight up the middle (only the faintest, gentle sway) ─
    const freeSteps = 42;
    for (let j = 1; j <= freeSteps; j++) {
      const kk = j / freeSteps;                          // 0 → 1
      const y = peelY - climb * kk;
      const pull = kk * kk * (3 - 2 * kk);               // smoothstep to centre
      const baseX = peelX + (cx - peelX) * pull;         // converge on the middle
      const sway = Math.sin(kk * 2.6 + time * 0.5) * 2.2 * kk;  // tiny, stays central
      pts.push([baseX + sway, y]);
    }

    // faint halo for softness
    ctx.strokeStyle = loopColor(0.75, 0.14);
    ctx.lineWidth = 3.4;
    ctx.beginPath();
    pts.forEach(([x, y], j) => j === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y));
    ctx.stroke();

    // the strand itself — a touch brighter/heavier so the eye follows it
    ctx.strokeStyle = loopColor(0.72, 0.92);
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    pts.forEach(([x, y], j) => j === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y));
    ctx.stroke();
  }

  // ─── Particles (material bursts) ───
  const particles = [];

  function emit(type, count) {
    for (let i = 0; i < count; i++) {
      const ang = Math.random() * Math.PI * 2;
      const sp  = type === 'fluff' ? 0.8 + Math.random() * 1.8
                : type === 'boll'  ? 1.4 + Math.random() * 2.4
                :                    1.8 + Math.random() * 2.8;
      particles.push({
        type,
        x: BX + Math.cos(ang) * R0 * 0.4,
        y: BY + Math.sin(ang) * R0 * 0.4,
        vx: Math.cos(ang) * sp * 1.3,
        vy: Math.sin(ang) * sp - (type === 'fluff' ? 0.6 : 0),
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.06,
        size: type === 'boll' ? 9 + Math.random() * 8
            : type === 'fluff' ? 10 + Math.random() * 10
            : 8 + Math.random() * 10,
        age: 0,
        life: type === 'fluff' ? 300 + Math.random() * 120
            : type === 'boll'  ? 240 + Math.random() * 100
            :                    200 + Math.random() * 90,
        ph: Math.random() * Math.PI * 2,
      });
    }
  }

  function drawStrand(p, alpha) {
    // a short crimped merino strand
    ctx.strokeStyle = loopColor(0.85, alpha);
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    const segs = 12;
    for (let j = 0; j <= segs; j++) {
      const k = j / segs;
      const x = (k - 0.5) * p.size * 3;
      const y = Math.sin(k * Math.PI * 4 + p.ph) * p.size * 0.32;
      j === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  function drawBoll(p, alpha) {
    const s = p.size;
    // sepals (dried calyx) behind
    ctx.fillStyle = `rgba(94,66,41,${alpha * 0.85})`;
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.6;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(a - 0.28) * s * 0.95, Math.sin(a - 0.28) * s * 0.95);
      ctx.lineTo(Math.cos(a) * s * 1.25, Math.sin(a) * s * 1.25);
      ctx.lineTo(Math.cos(a + 0.28) * s * 0.95, Math.sin(a + 0.28) * s * 0.95);
      ctx.closePath();
      ctx.fill();
    }
    // fluffy lobes
    ctx.fillStyle = `rgba(250,247,238,${alpha})`;
    const lobes = [[0, -0.42], [0.42, 0.05], [-0.42, 0.05], [0, 0.4], [0, -0.05]];
    for (const [lx, ly] of lobes) {
      ctx.beginPath();
      ctx.arc(lx * s, ly * s, s * 0.48, 0, Math.PI * 2);
      ctx.fill();
    }
    // soft creases
    ctx.strokeStyle = `rgba(180,170,150,${alpha * 0.5})`;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(0, -s * 0.5); ctx.lineTo(0, s * 0.5);
    ctx.moveTo(-s * 0.5, 0); ctx.lineTo(s * 0.5, 0);
    ctx.stroke();
  }

  function drawFluff(p, alpha) {
    const s = p.size;
    // bulrush seed: tiny seed + radiating pappus hairs (dandelion-like tuft)
    ctx.strokeStyle = loopColor(0.9, alpha * 0.8);
    ctx.lineWidth = 0.7;
    const hairs = 11;
    for (let i = 0; i < hairs; i++) {
      const a = -Math.PI / 2 + (i - (hairs - 1) / 2) * 0.24 + Math.sin(time + p.ph) * 0.04;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      const hx = Math.cos(a) * s, hy = Math.sin(a) * s;
      ctx.quadraticCurveTo(hx * 0.5, hy * 0.5 - s * 0.08, hx, hy);
      ctx.stroke();
      // tiny tip dots
      ctx.fillStyle = `rgba(250,247,238,${alpha * 0.7})`;
      ctx.beginPath();
      ctx.arc(hx, hy, 0.9, 0, Math.PI * 2);
      ctx.fill();
    }
    // seed
    ctx.fillStyle = `rgba(122,94,58,${alpha})`;
    ctx.beginPath();
    ctx.ellipse(0, s * 0.18, 1.6, 3.1, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  function stepParticles() {
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.age++;
      if (p.age > p.life) { particles.splice(i, 1); continue; }
      // physics per type
      if (p.type === 'boll')  { p.vy += 0.022; p.vx *= 0.988; p.vy *= 0.99; }
      if (p.type === 'fluff') { p.vy -= 0.006; p.vx += Math.sin(time * 1.4 + p.ph) * 0.02; p.vx *= 0.985; p.vy *= 0.985; }
      if (p.type === 'strand'){ p.vx *= 0.982; p.vy *= 0.982; }
      p.x += p.vx; p.y += p.vy;
      p.rot += p.vr;

      const fadeIn  = Math.min(1, p.age / 24);
      const fadeOut = Math.min(1, (p.life - p.age) / 70);
      const alpha = Math.min(fadeIn, fadeOut) * 0.95;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      if (p.type === 'strand') drawStrand(p, alpha);
      else if (p.type === 'boll') drawBoll(p, alpha);
      else drawFluff(p, alpha);
      ctx.restore();
    }
  }

  // ambient trickle while a burst chapter is active
  let activeBurst = null;
  let ambientTimer = 0;

  function drawWool() {
    time += 0.016;
    ctx.clearRect(0, 0, W, H);

    // ball is built purely from dense wound strands — no solid disc behind them
    const Rbase = ballPos();
    const R = Rbase * (1 - 0.62 * unravel);

    // windings
    const visF = LOOPS * (1 - Math.pow(unravel, 1.08));
    const vis  = Math.floor(visF);
    const frac = visF - vis;

    for (let i = 0; i < vis && i < LOOPS; i++) drawLoop(loops[i], R, 1);
    if (vis < LOOPS && frac > 0.02 && R > 3) {
      drawLoop(loops[vis], R, frac);
    }

    // the loose thread — anchored at the ball's centre (a stable point, so it
    // never jitters) and rising up through the windings, so it always reads as
    // one continuous piece of yarn emerging from the ball.
    if (unravel > 0.01) {
      drawTail(BX, BY, R, unravel);
    }

    // ambient particle trickle
    if (activeBurst && !reduced) {
      ambientTimer++;
      if (ambientTimer > 70) { emit(activeBurst, 1); ambientTimer = 0; }
    }

    stepParticles();
    if (!reduced) requestAnimationFrame(drawWool);
  }

  window.addEventListener('resize', () => { sizeCanvas(); });
  sizeCanvas();
  buildBall();
  if (reduced) { drawWool(); } else { requestAnimationFrame(drawWool); }
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && !reduced) requestAnimationFrame(drawWool);
  });

  // ═══════════════════════════════════════════════════════
  //  SCROLL ENGINE (chapters, atmosphere, HUD)
  // ═══════════════════════════════════════════════════════

  // reading position: the middle of each chapter's scrollable range
  const READ_FRAC = 0.5;

  // tall sections centre their reading column; scroll so that column lands mid-view
  function scrollToChapter(ch) {
    const vh = window.innerHeight;
    const tall = ch.offsetHeight > vh * 1.2;
    const top = tall ? ch.offsetTop + ch.offsetHeight / 2 - vh / 2 : ch.offsetTop;
    window.scrollTo({ top: Math.round(Math.max(0, top)), behavior: 'smooth' });
  }

  const heroEl = document.querySelector('.hero');

  // ─── Left depth rail (Surface → Deeper) ───
  (function depthRail() {
    const rail      = document.getElementById('depthRail');
    const railInner = rail.querySelector('.depth-inner');
    const ticksWrap = document.getElementById('depthTicks');
    const dot       = document.getElementById('depthDot');
    const label     = document.getElementById('depthLabel');

    const items = [
      { el: heroEl,      label: 'Surface'             },
      { el: chapters[0], label: 'Regenerative Merino' },
      { el: chapters[1], label: 'Fibregen'            },
      { el: chapters[2], label: 'BioPuff'             },
      { el: outro,       label: 'A single thread'     },
    ].filter(it => it.el);

    function targetY(it) {
      const vh = window.innerHeight;
      const maxY = Math.max(0, document.documentElement.scrollHeight - vh);
      const tall = it.el.offsetHeight > vh * 1.2;
      const y = tall ? it.el.offsetTop + it.el.offsetHeight / 2 - vh / 2 : it.el.offsetTop;
      return Math.min(Math.max(0, y), maxY);
    }

    // inset the ends so the first/last ticks don't sit on top of the bar's
    // rounded top & bottom (keeps the scrubber on the same inset track)
    const RAIL_PAD = 14;
    const posY = (frac, railH) => RAIL_PAD + frac * (railH - 2 * RAIL_PAD);

    let tickEls = [];
    function build() {
      ticksWrap.innerHTML = '';
      const maxY = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const railH = railInner.clientHeight;
      tickEls = items.map(it => {
        const frac = Math.min(1, Math.max(0, targetY(it) / maxY));
        const b = document.createElement('button');
        b.className = 'depth-tick';
        b.type = 'button';
        b.style.top = posY(frac, railH) + 'px';
        b.innerHTML = '<span class="dt-mark"></span><span class="dt-lab">' + it.label + '</span>';
        b.addEventListener('click', () => {
          window.scrollTo({ top: Math.round(targetY(it)), behavior: 'smooth' });
        });
        ticksWrap.appendChild(b);
        return b;
      });
    }

    let idleT = null;
    let dragging = false;
    function railUpdate() {
      const vh = window.innerHeight;
      const maxY = Math.max(1, document.documentElement.scrollHeight - vh);
      const railH = railInner.clientHeight;
      const y = window.scrollY;
      // visible only past the hero AND while recently active (or dragging);
      // otherwise it fades out — matching the homepage's idle behaviour
      const past = y > vh * 0.5;
      rail.classList.toggle('show', past && (rail.classList.contains('awake') || dragging));
      const frac = Math.min(1, Math.max(0, y / maxY));
      dot.style.top = posY(frac, railH) + 'px';
      label.style.top = posY(frac, railH) + 'px';
      let active = 0;
      items.forEach((it, i) => { if (y >= targetY(it) - vh * 0.35) active = i; });
      tickEls.forEach((t, i) => {
        t.classList.toggle('done', (targetY(items[i]) / maxY) <= frac + 0.005);
        t.classList.toggle('active', i === active);
      });
      label.textContent = items[active] ? items[active].label : '';
      const awake = rail.classList.contains('awake');
      label.style.opacity = (past && awake) ? '1' : '0';
    }

    function wake() {
      rail.classList.add('awake');
      clearTimeout(idleT);
      idleT = setTimeout(() => { rail.classList.remove('awake'); railUpdate(); }, 2200);
      railUpdate();
    }

    // drag the dot to scrub the page
    function scrubTo(clientY) {
      const r = railInner.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, (clientY - r.top) / r.height));
      const maxY = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo({ top: Math.round(frac * maxY) });
    }
    dot.addEventListener('pointerdown', e => {
      e.preventDefault();
      dragging = true; window.__railDragging = true;
      rail.classList.add('dragging'); wake();
      if (dot.setPointerCapture) dot.setPointerCapture(e.pointerId);
    });
    window.addEventListener('pointermove', e => { if (dragging) { scrubTo(e.clientY); wake(); } });
    window.addEventListener('pointerup', () => {
      if (!dragging) return;
      dragging = false; window.__railDragging = false;
      rail.classList.remove('dragging');
      // CSS scroll-snap settles the page to the nearest section on release
    });

    rail.addEventListener('pointerenter', wake);
    rail.addEventListener('pointermove', wake);
    window.addEventListener('scroll', wake, { passive: true });
    window.addEventListener('resize', () => { build(); railUpdate(); });
    window.addEventListener('load', () => { build(); railUpdate(); });
    build(); railUpdate();
    setTimeout(() => { build(); railUpdate(); }, 400);
  })();

  const markers = [];
  chapters.forEach((ch, i) => {
    const m = document.createElement('div');
    m.className = 'marker';
    m.style.top = ((i + 1) / (chapters.length + 1) * 100) + '%';
    m.addEventListener('click', (e) => { e.stopPropagation(); scrollToChapter(ch); });
    scrollBar.appendChild(m);
    markers.push(m);
  });

  function repositionMarkers() {
    const docH = document.documentElement.scrollHeight - window.innerHeight;
    if (docH <= 0) return;
    chapters.forEach((ch, i) => {
      const chScrollable = ch.offsetHeight - window.innerHeight;
      const midY = ch.offsetTop + chScrollable * 0.5;
      markers[i].style.top = Math.min(100, (midY / docH) * 100) + '%';
    });
  }
  setTimeout(repositionMarkers, 400);
  window.addEventListener('resize', repositionMarkers);

  scrollBar.addEventListener('click', (e) => {
    if (e.target !== scrollBar) return;
    const rect = scrollBar.getBoundingClientRect();
    const pct  = (e.clientY - rect.top) / rect.height;
    const idx  = Math.round(pct * (chapters.length - 1));
    scrollToChapter(chapters[Math.max(0, Math.min(chapters.length - 1, idx))]);
  });

  function hexToRgb(h) {
    h = h.replace('#', '');
    return [parseInt(h.substr(0,2),16), parseInt(h.substr(2,2),16), parseInt(h.substr(4,2),16)];
  }
  function lerpColor(a, b, t) {
    const A = hexToRgb(a), B = hexToRgb(b);
    return `rgb(${Math.round(A[0]+(B[0]-A[0])*t)}, ${Math.round(A[1]+(B[1]-A[1])*t)}, ${Math.round(A[2]+(B[2]-A[2])*t)})`;
  }
  const ease = t => t < 0.5 ? 2*t*t : 1 - Math.pow(-2*t+2, 2)/2;

  let ticking = false;
  const HERO_END  = '#a8c4b8';
  const OUTRO_TOP = '#141410';
  const OUTRO_BOT = '#050505';
  const YARN_TOTAL = 18911; // km of yarn — London to Lake Hawea, as the crow flies

  function update() {
    const vh = window.innerHeight;
    const docH = document.documentElement.scrollHeight - vh;
    const scrollY = window.scrollY;
    const globalPct = Math.max(0, Math.min(1, scrollY / docH));

    // The fixed HUD/depth-rail/wool-canvas/cursor have no inherent awareness
    // of where this section's content ends -- fade them out as the footer is
    // ABOUT TO enter view (wrapper.bottom <= vh), not only once it's already
    // fully on screen (wrapper.bottom <= 0) -- same fix/threshold as the
    // sidebar release on assets/care-and-guarantee.js and the pillnav fade on
    // assets/transparency-and-technology.js.
    if (wrapper) {
      const pastEnd = wrapper.getBoundingClientRect().bottom <= vh;
      wrapper.classList.toggle('past-end', pastEnd);
    }

    // unravel progress: starts as the hero leaves, completes at the outro
    const unravelEnd = outro.offsetTop - vh * 0.5;
    unravel = Math.max(0, Math.min(1, (scrollY - vh * 0.35) / (unravelEnd - vh * 0.35)));
    heroP = Math.max(0, Math.min(1, scrollY / (vh * 0.85)));

    // ball spins as thread pays out — gentler coupling + a slow idle drift
    const delta = scrollY - lastScroll;
    lastScroll = scrollY;
    spin += delta * 0.0012 + (reduced ? 0 : 0.00022);

    if (heroBg && scrollY < vh * 1.2) {
      heroBg.style.transform = `translateY(${scrollY * 0.32}px)`;
    }

    scrollBar.style.setProperty('--scroll-pct', (globalPct * 100) + '%');

    depthEl.textContent = Math.round(unravel * YARN_TOTAL).toLocaleString();

    // ─── Per-section reveal + active detection ───
    activeBurst = null;
    const centerY = vh * 0.5;

    chapters.forEach((ch, idx) => {
      const rect = ch.getBoundingClientRect();
      const secCenter = rect.top + rect.height / 2;
      // reveal: fully in when the section is centred, fading as it leaves.
      // Driven straight from scroll → no transition lag, no overlap.
      const dist = Math.abs(secCenter - centerY) / vh;
      const rp = Math.max(0, Math.min(1, 1 - dist * 1.5));
      const mat = materialByChapter[idx];
      if (mat) mat.style.setProperty('--rp', rp.toFixed(3));
      const figs = figsByChapter[idx];
      if (figs) figs.style.setProperty('--rp', rp.toFixed(3));

      // "active" = this section owns the viewport centre
      const active = rect.top <= centerY && rect.bottom >= centerY;
      if (markers[idx]) markers[idx].classList.toggle('active', active);

      if (active) {
        activeBurst = ch.dataset.burst || null;
        if (ch.dataset.ticker) tickerEl.textContent = ch.dataset.ticker;
        if (!ch._burstFired && !reduced) {
          const counts = { strand: 30, boll: 22, fluff: 26 };
          emit(ch.dataset.burst, counts[ch.dataset.burst] || 24);
          ch._burstFired = true;
        }
      } else if (rp < 0.12) {
        ch._burstFired = false;
      }
    });

    // ─── Atmosphere: interpolate smoothly across the colour stops ───
    // hero (teal) → each material's colours → outro (dark)
    const stops = [{ y: 0, top: HERO_END, bottom: HERO_END }];
    chapters.forEach(ch => {
      stops.push({
        y: ch.offsetTop + ch.offsetHeight / 2,
        top: ch.dataset.bgTop, bottom: ch.dataset.bgBottom,
      });
    });
    stops.push({ y: outro.offsetTop + vh * 0.5, top: OUTRO_TOP, bottom: OUTRO_BOT });

    const cY = scrollY + vh * 0.5;
    let a = stops[0], b = stops[0];
    if (cY <= stops[0].y) { a = b = stops[0]; }
    else if (cY >= stops[stops.length - 1].y) { a = b = stops[stops.length - 1]; }
    else {
      for (let i = 0; i < stops.length - 1; i++) {
        if (cY >= stops[i].y && cY <= stops[i + 1].y) { a = stops[i]; b = stops[i + 1]; break; }
      }
    }
    const t = a === b ? 0 : Math.max(0, Math.min(1, (cY - a.y) / (b.y - a.y)));
    atmosphere.style.setProperty('--bg-top', lerpColor(a.top, b.top, t));
    atmosphere.style.setProperty('--bg-bottom', lerpColor(a.bottom, b.bottom, t));

    if (scrollY < vh * 0.5) tickerEl.textContent = 'WOUND TIGHT · READY';
    else if (scrollY + vh >= document.documentElement.scrollHeight - 4)
      tickerEl.textContent = '∞ · A SINGLE THREAD · YOU ARE HERE';

    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }

  // ═══════════════════════════════════════════════════════
  //  CHIP INFO MODAL
  // ═══════════════════════════════════════════════════════
  (function chipModal() {
    const modal   = document.getElementById('chipModal');
    const titleEl = document.getElementById('modalTitle');
    const bodyEl  = document.getElementById('modalBody');
    const imgEl   = document.getElementById('modalImg');
    const closeBtn= document.getElementById('modalClose');
    if (!modal) return;
    let lastFocus = null;
    imgEl.addEventListener('load', () => revealModalImg(imgEl));

    function open(btn) {
      titleEl.innerHTML = btn.dataset.title || '';
      bodyEl.textContent = btn.dataset.info || '';
      hideModalImg(imgEl);
      // the image URL is already resolved to the theme's CDN by the section's
      // Liquid markup (data-img="{{ 'file.jpg' | asset_url }}")
      imgEl.src = btn.dataset.img || '';
      if (imgEl.complete) revealModalImg(imgEl);
      modal.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      lastFocus = btn;
      closeBtn.focus();
    }
    function close() {
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      if (lastFocus) lastFocus.focus();
    }
    document.querySelectorAll('.chip').forEach(btn => {
      btn.addEventListener('click', () => open(btn));
    });
    closeBtn.addEventListener('click', close);
    modal.addEventListener('click', e => { if (e.target === modal) close(); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.classList.contains('open')) close();
    });
  })();

  // ═══════════════════════════════════════════════════════
  //  IMAGE LIGHTBOX (tap a figure → larger image + explanation)
  // ═══════════════════════════════════════════════════════
  (function lightbox() {
    const modal   = document.getElementById('imgModal');
    const imgEl   = document.getElementById('imgModalImg');
    const titleEl = document.getElementById('imgModalTitle');
    const capEl   = document.getElementById('imgModalCap');
    const closeBtn= document.getElementById('imgModalClose');
    if (!modal) return;
    let lastFocus = null;
    imgEl.addEventListener('load', () => revealModalImg(imgEl));

    function open(fig) {
      const img = fig.querySelector('img');
      const cap = fig.querySelector('figcaption');
      hideModalImg(imgEl);
      imgEl.src = img ? img.currentSrc || img.src : '';
      if (imgEl.complete) revealModalImg(imgEl);
      imgEl.alt = img ? img.alt : '';
      titleEl.textContent = cap ? cap.textContent : '';
      capEl.textContent = fig.dataset.explain || '';
      modal.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      lastFocus = fig;
      closeBtn.focus();
    }
    function close() {
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    document.querySelectorAll('.m-fig').forEach(fig => {
      fig.setAttribute('tabindex', '0');
      fig.setAttribute('role', 'button');
      fig.addEventListener('click', () => open(fig));
      fig.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(fig); }
      });
    });
    closeBtn.addEventListener('click', close);
    modal.addEventListener('click', e => { if (e.target === modal) close(); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.classList.contains('open')) close();
    });
  })();

  // ═══════════════════════════════════════════════════════
  //  TYPEWRITER INTERLUDES (fact types on, cursor blinks)
  // ═══════════════════════════════════════════════════════
  (function interludes() {
    const items = Array.from(document.querySelectorAll('.interlude'));
    if (!items.length) return;

    function typeOut(el) {
      if (el._typed) return;
      el._typed = true;
      const textEl = el.querySelector('.fact-text');
      const note   = el.querySelector('.fact-note');
      const full   = el.dataset.fact || '';
      const noteTxt= el.dataset.note || '';
      if (reduced) {
        textEl.textContent = full;
        note.textContent = noteTxt;
        note.classList.add('show');
        return;
      }
      let i = 0;
      (function tick() {
        textEl.textContent = full.slice(0, i);
        i++;
        if (i <= full.length) {
          setTimeout(tick, 46 + Math.random() * 42);
        } else {
          note.textContent = noteTxt;
          setTimeout(() => note.classList.add('show'), 280);
        }
      })();
    }

    // fire only once the interlude has reached the middle of the viewport,
    // i.e. when the reader has actually arrived at that section
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) typeOut(e.target); });
    }, { rootMargin: '-38% 0px -38% 0px', threshold: 0 });
    items.forEach(el => io.observe(el));
  })();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  update();
})();
